import React, {
	Dispatch,
	memo,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import cn from 'classnames';

import {
	AdaptiveSingleLineText,
	Button,
	Confirm,
	Modal,
	SpinnerOverlay,
	SubmissionButtons } from '@components/common';
import CustomValue from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/FieldsMapping/CustomValue';
import SystemFieldName
	from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/FieldsMapping/SystemFieldName';

import { useMutateSetAssetsCommonValues } from '@hooks/assets/useMutateSetAssetsCommonValues';
import { useQueryAssetsGroupSystemFields } from '@hooks/assets/useQueryAssetsGroupSystemFields';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	generateDefaultFormFieldCustomValues,
	generateFormFieldCustomValues,
} from '@utils/customValueUtils/customValueUtils';

import { PreparedCommonValues, TCommonValue, TSelectedField } from '@interfaces/import/import';

import { EMenuActionsTypes, ESnackbarTypes, TMenuActionsTypes } from '@enums/common';

import { responseMessages } from '@constants/responseMessages';

import styles from './CommonValuesMapping.module.scss';

interface ICommonValuesMappingProps {
	onClose: () => void;
	closeSetCommonValues: () => void;
	setIsDirty: Dispatch<SetStateAction<boolean>>;
	isDirtyForm?: boolean;
	activeGroupTableAction?: TMenuActionsTypes;
	selectedAssetsIds?: Array<string>;
	filter?: Record<string, string | number | boolean>;
}

const CommonValuesMapping = ({
	onClose,
	closeSetCommonValues,
	setIsDirty,
	isDirtyForm,
	activeGroupTableAction,
	selectedAssetsIds,
	filter,
}: ICommonValuesMappingProps) => {
	const dispatch = useAppDispatch();

	const { systemFields, isLoading } = useQueryAssetsGroupSystemFields();
	const { saveAssetsCommonValues, isLoadingSaveAssetsCommonValues }
		= useMutateSetAssetsCommonValues({ onClose: closeSetCommonValues });

	const [formFields, setFormFields] = useState(() => generateDefaultFormFieldCustomValues(systemFields));

	const [isDirtyCustomFields, setIsDirtyCustomFields] = useState(false);
	const [isOpenSaveConfirm, setIsOpenSaveConfirm] = useState(false);

	const resetAllFields = useCallback(() => {
		setFormFields(() => generateDefaultFormFieldCustomValues(systemFields));
		setIsDirty(false);
	}, [setIsDirty, systemFields]);

	useEffect(() => resetAllFields(), [resetAllFields]);

	const fieldsMappingValues: TSelectedField[] = useMemo(() => Object.values(formFields), [formFields]);

	const isEmptyAllFields = useMemo(() =>
		!fieldsMappingValues.filter(({ defaultValue }) => defaultValue !== null).length, [fieldsMappingValues]);

	const onChangeCustomValue = ({
		fieldName,
		defaultValue,
	}: {
		fieldName: string;
		defaultValue: TCommonValue | null;
	}) => {
		if (!isDirtyForm) setIsDirty(true);

		setFormFields(prevState => ({
			...prevState,
			...generateFormFieldCustomValues({ fieldName, defaultValue, formFields }),
		}));
	};

	const closeConfirm = () => setIsOpenSaveConfirm(false);

	const openConfirm = () => setIsOpenSaveConfirm(true);

	const onSave = () => {
		setIsDirtyCustomFields(false);
		closeConfirm();

		const isValid = Object.values(formFields).every((field) => {
			const isRequired = !!field?.isRequired;
			const defaultValue = field?.defaultValue;
			const maxValue = field.systemFieldsDefaultData?.maxValue;

			return (!isRequired && !maxValue) ||
				(
					maxValue
						? (Number(field.defaultValue) <= maxValue)
						: (defaultValue !== null && defaultValue !== undefined)
				);
		});

		if (!isValid) {
			setIsDirtyCustomFields(true);

			dispatch(setSnackbar({
				message: responseMessages.errorFillAll,
				type: ESnackbarTypes.error,
			}));
		} else {
			const commonValues: PreparedCommonValues[] = Object.values(formFields)
				.filter(field => field.defaultValue !== null)
				.map((field) => ({
					systemFieldName: field.systemFieldName,
					value: field.defaultValue!,
				}));

			saveAssetsCommonValues({
				values: commonValues,
				assetIds: selectedAssetsIds,
				isAll: activeGroupTableAction === EMenuActionsTypes.all
					|| activeGroupTableAction === EMenuActionsTypes.allFiltered,
				filter: activeGroupTableAction === EMenuActionsTypes.allFiltered && filter
					? filter
					: null,
			});
		}
	};

	return (
		<>
			{(isLoading || isLoadingSaveAssetsCommonValues) && <SpinnerOverlay />}
			<Modal
				isOpen={isOpenSaveConfirm}
				onClose={closeConfirm}
			>
				<Confirm
					onClose={closeConfirm}
					onSubmit={onSave}
					question='Are you sure you want to set common values for the selected assets?'
				>
					Previous data will be rewritten.
				</Confirm>
			</Modal>
			<div className={styles.firstRow}>
				<div className={styles.title}>Asset Attributes</div>
				<div className={cn(styles.title, styles.offset5)}>Common value</div>
				<div className={cn(styles.title, styles.offset15)}>Example</div>
			</div>
			<div className={styles.commonValuesMappingContainer}>
				{!!fieldsMappingValues?.length && fieldsMappingValues.map((field) => {
					return (
						<div
							key={field.systemFieldName}
							className={styles.row}
						>
							<SystemFieldName
								isRequired={field.isRequired}
								name={field.title}
								tooltipText={field.tooltip}
							/>

							<div className={styles.defaultValue}>
								{field.hasDefaultValue
									&& <CustomValue
										customChangeHandler={onChangeCustomValue}
										field={field}
										isCommonDirty={isDirtyCustomFields}
										isWithSlider={false}
										menuPlacement='bottom'
									/>
								}
							</div>
							<div className={styles.example}>
								{!!field.example.length
									&& <AdaptiveSingleLineText
										maxWidth={200}
										paddingsSize={0}
										text={field.example}
									/>
								}
							</div>
						</div>
					);
				})}
			</div>
			<div className={styles.buttons}>
				<Button
					disabled={isEmptyAllFields}
					onClick={resetAllFields}
					text='Reset all'
					withoutBackground
				/>
				<SubmissionButtons
					disabledPositive={isEmptyAllFields}
					negativeHandler={onClose}
					positiveHandler={openConfirm}
					positiveLabel='Save'
				/>
			</div>
		</>
	);
};

export default memo(CommonValuesMapping);
